/**
 * PermissionEditor - Composant d'édition des permissions
 */

import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import {
  Shield, Package, FileText, MessageSquare, CreditCard, BarChart2,
  Settings, AlertTriangle, Truck, Navigation, Users
} from 'lucide-react';

// Icônes par module
const MODULE_ICONS = {
  operations: Package,
  quotes: FileText,
  users: Users,
  messaging: MessageSquare,
  finances: CreditCard,
  reports: BarChart2,
  settings: Settings,
  roles: Shield,
  incidents: AlertTriangle,
  fleet: Truck,
  tracking: Navigation,
};

const PermissionEditor = ({ 
  permissionCategories, 
  selectedPermissions, 
  onToggle, 
  onToggleCategory 
}) => {
  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-4">
        {permissionCategories.map(category => {
          const ModuleIcon = MODULE_ICONS[category.id] || Shield;
          const catPermissions = category.permissions || [];
          const selectedCount = catPermissions.filter(p => selectedPermissions.includes(p.id)).length;
          const allSelected = catPermissions.length > 0 && selectedCount === catPermissions.length;
          
          return (
            <Card key={category.id} className="border">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ModuleIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{category.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {selectedCount}/{catPermissions.length}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleCategory(category.id, catPermissions)}
                    className="text-xs"
                  >
                    {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-2 px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {catPermissions.map(perm => (
                    <label
                      key={perm.id}
                      className={`flex items-start gap-2 p-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedPermissions.includes(perm.id) ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <Checkbox
                        checked={selectedPermissions.includes(perm.id)}
                        onCheckedChange={() => onToggle(perm.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium">{perm.name}</span>
                          {perm.scope && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                perm.scope === 'all' ? 'bg-red-50 text-red-700' :
                                perm.scope === 'assigned' ? 'bg-blue-50 text-blue-700' :
                                'bg-gray-50 text-gray-700'
                              }`}
                            >
                              {perm.scope === 'all' ? 'Tout' : 
                               perm.scope === 'assigned' ? 'Assigné' : 'Propre'}
                            </Badge>
                          )}
                        </div>
                        {perm.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export default PermissionEditor;
